import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';

export type MisReportStatus = 'draft' | 'submitted' | 'approved';

export interface MisReportAsset {
  item: string;
  cost: number;
}

export interface MisReport {
  id: string;
  legacyId: number | null;
  schoolId: string;
  school?: { id: string; name: string };
  month: string;
  year: number;
  districtName: string | null;
  incomeData: Record<string, number>;
  expenseData: Record<string, number>;
  totalIncome: string;
  totalExpense: string;
  closingCashBank: string;
  q1ProperDocuments: boolean;
  q1Details: string | null;
  q2FeeReceivable: string | null;
  q3AmountPayable: string | null;
  q4AssetsPurchased: MisReportAsset[] | null;
  q5PreviousYearFees: string | null;
  q6PendingPaymentHo: string | null;
  pdfFilePath: string | null;
  status: MisReportStatus;
  createdByUserId: string | null;
  createdByName?: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type MisReportSortField = 'month' | 'createdAt' | 'totalIncome' | 'totalExpense';

export interface MisReportListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: MisReportSortField;
  sortOrder?: 'asc' | 'desc';
  schoolId?: string;
  status?: MisReportStatus;
}

export interface MisReportPayload {
  schoolId?: string;
  month?: string;
  districtName?: string;
  incomeData: Record<string, number>;
  expenseData: Record<string, number>;
  q1ProperDocuments?: boolean;
  q1Details?: string;
  q2FeeReceivable?: number;
  q3AmountPayable?: number;
  q4AssetsPurchased?: MisReportAsset[];
  q5PreviousYearFees?: number;
  q6PendingPaymentHo?: number;
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export const MisReportsApi = {
  list: async (params?: MisReportListParams): Promise<PaginatedResult<MisReport>> =>
    unwrap(await api.get('/mis-reports', { params })),
  get: async (id: string): Promise<MisReport> => unwrap(await api.get(`/mis-reports/${id}`)),
  create: async (data: MisReportPayload): Promise<MisReport> =>
    unwrap(await api.post('/mis-reports', data)),
  update: async (id: string, data: Partial<MisReportPayload>): Promise<MisReport> =>
    unwrap(await api.patch(`/mis-reports/${id}`, data)),
  remove: async (id: string): Promise<{ id: string }> =>
    unwrap(await api.delete(`/mis-reports/${id}`)),
  pdf: async (id: string): Promise<{ pdfFilePath: string }> =>
    unwrap(await api.get(`/mis-reports/${id}/pdf`)),
  /** Re-pulls fresh income/expense sums into a DRAFT report only — rejected if already submitted/approved. */
  regenerateData: async (id: string): Promise<MisReport> =>
    unwrap(await api.post(`/mis-reports/${id}/regenerate-data`)),
};
